import { useState, useCallback } from "react";
import { Container, Row, Accordion } from "react-bootstrap";
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography, TextField } from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import BarChartIcon from "@mui/icons-material/BarChart";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import GroupsIcon from "@mui/icons-material/Groups";
import HistoryIcon from "@mui/icons-material/History";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import { MaterialReactTable, useMaterialReactTable, MRT_ColumnDef } from "material-react-table";
import Swal from "sweetalert2";
import dayjs from "dayjs";

import {
  CUSTOMER_CONFIG, FUTURES_CONFIG, SWAP_CONFIGS, BOOK_COLORS, HISTORY_LIMIT,
} from "./pnlOperations.constants";
import { runPnlOperation, formatDuration, parseRecords } from "./pnlOperations.actions";
import {
  ExecutionHistoryRow, OperationRunState, PnlOperationConfig,
} from "./pnlOperations.types";

/* ---------- design tokens ---------- */
const BRAND = "#0891a8";
const BRAND_HOVER = "#067c90";
const BRAND_SOFT = "#e6f5f8";
const INK = "#1f2937";
const MUTED = "#6b7280";
const BORDER = "#e5e9ef";
const SURFACE = "#ffffff";
const SURFACE_ALT = "#f7f9fb";
const RADIUS = "8px";
const RADIUS_SM = "6px";
const SHADOW = "0 1px 2px rgba(16,24,40,0.04)";

const STATUS: Record<string, { bg: string; fg: string; dot: string }> = {
  Success: { bg: "#e8f7ee", fg: "#15803d", dot: "#22c55e" },
  Failed: { bg: "#fdecec", fg: "#b91c1c", dot: "#ef4444" },
  Running: { bg: "#fff4e0", fg: "#b45309", dot: "#f59e0b" },
};

const TYPE_CHIP: Record<string, { bg: string; fg: string; label: string }> = {
  FUTURES: { bg: "#e5eefe", fg: "#1d4ed8", label: "Futures" },
  SWAPS: { bg: "#efeafe", fg: "#6d28d9", label: "Swaps" },
  CUSTOMER: { bg: "#fde8ed", fg: "#be123c", label: "Customer" },
};

const panelSx = {
  width: "100%",
  textAlign: "left",
  bgcolor: SURFACE,
  border: `1px solid ${BORDER}`,
  borderRadius: RADIUS,
  boxShadow: SHADOW,
  p: 1.25,
};

const solidBtnSx = {
  height: 30,
  px: 1.75,
  minWidth: 150,
  bgcolor: BRAND,
  color: "#fff",
  textTransform: "none",
  fontSize: "0.75rem",
  fontWeight: 600,
  borderRadius: RADIUS_SM,
  boxShadow: "none",
  whiteSpace: "nowrap",
  "&:hover": { bgcolor: BRAND_HOVER, boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: "#a5d6df", color: "#fff" },
};

const tonalBtnSx = {
  ...solidBtnSx,
  minWidth: 0,
  width: "100%",
  bgcolor: BRAND_SOFT,
  color: BRAND,
  "&:hover": { bgcolor: BRAND, color: "#fff", boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: "#eef3f5", color: "#9ca3af" },
};

/* ---------- building blocks ---------- */
const StatusChip = ({ status }: { status: string }) => {
  const c = STATUS[status] ?? STATUS.Running;
  return (
    <Box
      sx={{
        display: "inline-flex", alignItems: "center", gap: 0.6, px: 0.9, height: 20,
        borderRadius: "10px", bgcolor: c.bg, color: c.fg, fontSize: "0.68rem", fontWeight: 600,
      }}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: c.dot }} />
      {status}
    </Box>
  );
};

const Stat = ({ label, children }: { label: string; children?: React.ReactNode }) => (
  <Box sx={{ minWidth: 0, textAlign: "left !important", display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
    <Typography sx={{ fontSize: "0.65rem", lineHeight: 1.3, color: MUTED, fontWeight: 500, textAlign: "left !important" }}>
      {label}
    </Typography>
    <Typography
      component="div"
      sx={{ fontSize: "0.76rem", fontWeight: 600, lineHeight: 1.6, color: INK, fontVariantNumeric: "tabular-nums", textAlign: "left !important" }}
    >
      {children ?? <span style={{ color: "#b0b7c3" }}>–</span>}
    </Typography>
  </Box>
);

/** One strip component for every card: equal columns, hairline dividers, left aligned */
const StatsStrip = ({ items, cols }: { items: { label: string; value?: React.ReactNode }[]; cols: string }) => (
  <Box
    sx={{
      display: "grid", gridTemplateColumns: cols, bgcolor: SURFACE_ALT,
      border: `1px solid ${BORDER}`, borderRadius: RADIUS_SM, py: 0.6, mt: 1,
    }}
  >
    {items.map((it, i) => (
      <Box key={it.label} sx={{ px: 1.25, borderLeft: i ? `1px solid ${BORDER}` : "none" }}>
        <Stat label={it.label}>{it.value}</Stat>
      </Box>
    ))}
  </Box>
);

const IconTile = ({ children, color = BRAND, bg = BRAND_SOFT }: { children: React.ReactNode; color?: string; bg?: string }) => (
  <Box
    sx={{
      width: 34, height: 34, flexShrink: 0, borderRadius: RADIUS_SM, bgcolor: bg, color,
      display: "flex", alignItems: "center", justifyContent: "center",
      "& svg": { fontSize: 20 },
    }}
  >
    {children}
  </Box>
);

/** Same header pattern for every card: icon · title/description · optional action */
const PanelHeader = ({
  icon, title, description, action,
}: { icon: React.ReactNode; title: string; description: string; action?: React.ReactNode }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
    <IconTile>{icon}</IconTile>
    <Box sx={{ flex: 1, minWidth: 0, textAlign: "left !important" }}>
      <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, lineHeight: 1.3, color: INK, textAlign: "left !important" }}>{title}</Typography>
      <Typography sx={{ fontSize: "0.72rem", lineHeight: 1.4, color: MUTED, textAlign: "left !important" }}>{description}</Typography>
    </Box>
    {action}
  </Box>
);

/* ---------- component ---------- */
const PnlOperations = () => {
  const today = dayjs().format("YYYY-MM-DD");
  const [pnlDate, setPnlDate] = useState(today);
  const [runState, setRunState] = useState<Record<string, OperationRunState>>({});
  const [running, setRunning] = useState<Record<string, boolean>>({});
  const [history, setHistory] = useState<ExecutionHistoryRow[]>([]);

  const handleRun = useCallback(
    async (cfg: PnlOperationConfig) => {
      const startedAt = dayjs();
      setRunning((p) => ({ ...p, [cfg.key]: true }));
      let row: ExecutionHistoryRow;
      try {
        const res = await runPnlOperation(cfg, pnlDate);
        const ok = res.status === "success" && !res.fail_count;
        // prefer backend-reported time; fall back to client-side diff
        const duration = res.total_seconds !== undefined
          ? formatDuration(res.total_seconds * 1000)
          : formatDuration(dayjs().diff(startedAt));
        const total_trades = res.total_trades
        setRunState((p) => ({
          ...p,
          [cfg.key]: {
            status: ok ? "Success" : "Failed",
            lastRun: startedAt.format("DD MMM YYYY HH:mm"),
            total_trades, duration, message: res.message,
          },
        }));
        row = {
          id: crypto.randomUUID(), operation: cfg.buttonLabel, type: cfg.type, book: cfg.book,
          startedAt: startedAt.format("DD MMM YYYY HH:mm:ss"),
          completedAt: dayjs().format("DD MMM YYYY HH:mm:ss"),
          status: ok ? "Success" : "Failed",total_trades, duration,
          triggeredBy: "System Admin", response: res,
        };

        // skipped (futures/swaps) or failed (customer) trades
        const problems = res.skipped_trades ?? res.failed_trades ?? [];
        const problemCount = res.skipped ?? res.fail_count ?? 0;
        if (problemCount) {
          Swal.fire({
            icon: "warning",
            title: `${problemCount} trade(s) ${res.fail_count ? "failed" : "skipped"}`,
            html: problems
              .map((t: any) => `${t.exec_id ?? "-"}: ${t.reason ?? JSON.stringify(t)}`)
              .join("<br/>"),
          });
        }
      } catch (e: any) {
        setRunState((p) => ({
          ...p,
          [cfg.key]: { status: "Failed", lastRun: startedAt.format("DD MMM YYYY HH:mm") },
        }));
        row = {
          id: crypto.randomUUID(), operation: cfg.buttonLabel, type: cfg.type, book: cfg.book,
          startedAt: startedAt.format("DD MMM YYYY HH:mm:ss"),
          status: "Failed", triggeredBy: "System Admin",
        };
        Swal.fire({ icon: "error", title: `${cfg.title} failed`, text: e?.message ?? "Unknown error" });
      } finally {
        setRunning((p) => ({ ...p, [cfg.key]: false }));
      }
      setHistory((p) => [row, ...p].slice(0, HISTORY_LIMIT));
    },
    [pnlDate]
  );

  const renderRunButton = (cfg: PnlOperationConfig, variant: "solid" | "tonal" = "solid") => (
    <Button
      variant="contained"
      disableElevation
      sx={variant === "solid" ? solidBtnSx : tonalBtnSx}
      disabled={running[cfg.key] || !cfg.endpoint}
      startIcon={
        running[cfg.key]
          ? <CircularProgress size={13} color="inherit" />
          : <PlayArrowRoundedIcon sx={{ fontSize: "18px !important" }} />
      }
      onClick={() => handleRun(cfg)}
    >
      {cfg.buttonLabel}
    </Button>
  );

  const statusNode = (key: string) =>
    running[key] ? <StatusChip status="Running" /> : runState[key] ? <StatusChip status={runState[key].status} /> : undefined;

  const renderStrip = (key: string) => {
    const s = runState[key];
    return (
      <StatsStrip
        cols="repeat(4, 1fr)"
        items={[
          { label: "Last run", value: s?.lastRun },
          { label: "Status", value: statusNode(key) },
          { label: "Records processed", value: s?.records },
          { label: "Duration", value: s?.duration },
        ]}
      />
    );
  };

  const renderTileStrip = (key: string) => {
    const s = runState[key];
    return (
      <StatsStrip
        cols="1.6fr 1.1fr 0.7fr"
        items={[
          { label: "Last run", value: s?.lastRun },
          { label: "Status", value: statusNode(key) },
          { label: "Records", value: s?.total_trades
         },
        ]}
      />
    );
  };

  const columns: MRT_ColumnDef<ExecutionHistoryRow>[] = [
    { header: "Operation", accessorKey: "operation", size: 150 },
    {
      header: "Type", accessorKey: "type", size: 100,
      Cell: ({ cell }) => {
        const v = cell.getValue<string>();
        const c = TYPE_CHIP[v] ?? { bg: "#eee", fg: "#333", label: v };
        return (
          <Box sx={{ display: "inline-block", px: 0.9, height: 20, lineHeight: "20px", borderRadius: "4px", bgcolor: c.bg, color: c.fg, fontSize: "0.68rem", fontWeight: 600 }}>
            {c.label}
          </Box>
        );
      },
    },
    { header: "Book", accessorKey: "book", size: 70, Cell: ({ cell }) => cell.getValue<string>() ?? "–" },
    { header: "Started", accessorKey: "startedAt", size: 150 },
    { header: "Completed", accessorKey: "completedAt", size: 150, Cell: ({ cell }) => cell.getValue<string>() ?? "–" },
    { header: "Status", accessorKey: "status", size: 100, Cell: ({ cell }) => <StatusChip status={cell.getValue<string>()} /> },
    { header: "Records", accessorKey: "records", size: 80, Cell: ({ cell }) => cell.getValue<number>() ?? "–" },
    { header: "Duration", accessorKey: "duration", size: 80, Cell: ({ cell }) => cell.getValue<string>() ?? "–" },
    { header: "Triggered by", accessorKey: "triggeredBy", size: 120 },
  ];

  const table = useMaterialReactTable({
    columns,
    data: history,
    enableRowNumbers: true,
    rowNumberDisplayMode: "static",
    displayColumnDefOptions: {
      "mrt-row-numbers": { header: "#", size: 36 },
      "mrt-row-actions": { header: "", size: 50 },
    },
    enableRowActions: true,
    positionActionsColumn: "last",
    renderRowActions: ({ row }) => (
      <Tooltip title="View response">
        <IconButton
          size="small"
          onClick={() =>
            Swal.fire({
              title: row.original.operation,
              html: `<pre style="text-align:left;font-size:12px;max-height:320px;overflow:auto">${JSON.stringify(
                row.original.response ?? { status: row.original.status }, null, 2
              )}</pre>`,
              width: 600,
            })
          }
        >
          <VisibilityOutlinedIcon sx={{ fontSize: 16, color: MUTED }} />
        </IconButton>
      </Tooltip>
    ),
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enablePagination: false,
    enableColumnActions: false,
    enableSorting: false,
    enableDensityToggle: false,
    enableStickyHeader: true,
    initialState: { density: "compact" },
    muiTablePaperProps: { elevation: 0, sx: { boxShadow: "none", bgcolor: "transparent" } },
    muiTableContainerProps: { sx: { maxHeight: 260, border: `1px solid ${BORDER}`, borderRadius: RADIUS_SM } },
    muiTableHeadCellProps: {
      sx: {
        fontSize: "0.68rem", fontWeight: 600, color: MUTED, bgcolor: SURFACE_ALT,
        py: 0.6, borderBottom: `1px solid ${BORDER}`, textAlign: "left",
      },
    },
    muiTableBodyCellProps: {
      sx: { fontSize: "0.72rem", color: INK, py: 0.4, borderBottom: `1px solid ${BORDER}`, textAlign: "left", fontVariantNumeric: "tabular-nums" },
    },
    muiTableBodyRowProps: { sx: { "&:hover td": { bgcolor: "#f3fafb" } } },
    renderEmptyRowsFallback: () => (
      <Typography sx={{ p: 2, fontSize: "0.74rem", textAlign: "center", color: MUTED }}>
        No runs yet. Run an operation above and it will appear here.
      </Typography>
    ),
  });

  return (
    <div className="page-body">
      <Container fluid>
        <Row>
          <div>
            <div className="table-ad">
              <Accordion defaultActiveKey="0" className="table-ad__addInputs">
                <Accordion.Item eventKey="0">
                  <Accordion.Header className="accordion_button">P&L Operations</Accordion.Header>
                  <Accordion.Body>
                    <div className="form_contain">
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, width: "100%", textAlign: "left" }}>

                        {/* Toolbar: subtitle + P&L date */}
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                          <Typography sx={{ fontSize: "0.78rem", color: MUTED }}>
                            Run P&L calculations for futures, swaps and customer P&L.
                          </Typography>
                        </Box>

                        {/* FUTURES */}
                        <Box sx={panelSx}>
                          <PanelHeader
                            icon={<BarChartIcon />}
                            title="Futures P&L"
                            description="Run futures P&L calculations for all books."
                            action={renderRunButton(FUTURES_CONFIG)}
                          />
                          {renderStrip(FUTURES_CONFIG.key)}
                        </Box>

                        {/* SWAPS */}
                        <Box sx={panelSx}>
                          <PanelHeader
                            icon={<SwapHorizIcon />}
                            title="Swaps P&L"
                            description="Run swaps P&L calculations by book."
                          />
                          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 1, mt: 1 }}>
                            {SWAP_CONFIGS.map((cfg) => {
                              const c = BOOK_COLORS[cfg.book!];
                              return (
                                <Box
                                  key={cfg.key}
                                  sx={{
                                    border: `1px solid ${BORDER}`, borderTop: `3px solid ${c.fg}`,
                                    borderRadius: RADIUS_SM, p: 1, bgcolor: SURFACE,
                                  }}
                                >
                                  <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
                                    <Box
                                      sx={{
                                        width: 28, height: 28, borderRadius: "6px", bgcolor: c.bg, color: c.fg,
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        fontWeight: 700, fontSize: "0.85rem", flexShrink: 0,
                                      }}
                                    >
                                      {cfg.book![0]}
                                    </Box>
                                    <Box sx={{ minWidth: 0, textAlign: "left !important" }}>
                                      <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, lineHeight: 1.25, color: INK, textAlign: "left !important" }}>{cfg.book}</Typography>
                                      <Typography sx={{ fontSize: "0.68rem", lineHeight: 1.3, color: MUTED, textAlign: "left !important" }} noWrap>{cfg.description}</Typography>
                                    </Box>
                                  </Box>
                                  {renderRunButton(cfg, "tonal")}
                                  {renderTileStrip(cfg.key)}
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>

                        {/* CUSTOMER */}
                        <Box sx={panelSx}>
                          <PanelHeader
                            icon={<GroupsIcon />}
                            title="Customer P&L"
                            description="Run customer-level P&L calculation after swaps P&L."
                            action={renderRunButton(CUSTOMER_CONFIG)}
                          />
                          {renderStrip(CUSTOMER_CONFIG.key)}
                        </Box>

                        {/* HISTORY */}
                        <Box sx={panelSx}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                              <IconTile><HistoryIcon /></IconTile>
                              <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: INK }}>Recent execution history</Typography>
                            </Box>
                            <Button
                              variant="outlined"
                              startIcon={<FormatListBulletedIcon sx={{ fontSize: "16px !important" }} />}
                              sx={{
                                textTransform: "none", fontSize: "0.72rem", fontWeight: 500, color: INK,
                                borderColor: BORDER, borderRadius: RADIUS_SM, height: 28, px: 1.25,
                                "&:hover": { borderColor: BRAND, bgcolor: BRAND_SOFT, color: BRAND },
                              }}
                              // TODO: navigate to the System Logs page
                              onClick={() => {}}
                            >
                              View all logs
                            </Button>
                          </Box>
                          <MaterialReactTable table={table} />
                        </Box>

                      </Box>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </div>
          </div>
        </Row>
      </Container>
    </div>
  );
};

export default PnlOperations;